import type { ModuleDefinition, TileState } from '@platform/entitlements';

export type { ModuleDefinition, ModuleId, ModuleSection, TileState } from '@platform/entitlements';

/** A module as the portal renders it: catalogue definition plus this user's access state. */
export interface PortalModule extends ModuleDefinition {
  state: TileState;
}

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  assignedModules: string[];
  mustChangePassword: boolean;
  isAdmin: boolean;
  /** Talogent staff, by email domain — sees the Talogent section. */
  canAccessTalogent: boolean;
}

export interface InstallInfo {
  clientName: string;
  environment: string;
  platformVersion: string;
}

export interface SsoProviderStatus {
  google: boolean;
  entra: boolean;
}

export interface SessionResponse {
  authenticated: boolean;
  authMode: 'dev' | 'local' | 'oidc' | 'handoff';
  user: SessionUser | null;
  install: InstallInfo;
  ssoProviders: SsoProviderStatus;
  /** Present on demo instances: where signed-out users are sent to sign in. */
  gatewayUrl?: string;
}

export interface ModulesResponse {
  modules: PortalModule[];
}

// --- Administration ---------------------------------------------------------

export interface CompanyProfile {
  company_name: string;
  legal_name: string | null;
  logo_url: string | null;
  email_domains: string[];
  locale: string;
  timezone: string;
  support_contact: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country: string | null;
  /** Most significant first — the first entry is the primary classification. */
  sic_codes: string[];
  updated_at: string | null;
}

export interface EntitlementRow {
  module_key: string;
  name: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
  /** Permanent seats. 0 = unavailable, 1-9998 = named users, 9999 = unlimited. */
  seat_limit: number;
  seats_used: number;
  /** null when unlimited. */
  seats_available: number | null;
  /** Temporary licences, counted separately from permanent seats. */
  temporary_seat_limit: number;
  temporary_seats_used: number;
  temporary_seats_available: number | null;
  /** Enabled AND holding at least one seat. */
  available: boolean;
  unlimited: boolean;
  term_start: string | null;
  term_end: string | null;
  in_catalog: boolean;
  updated_at: string;
  updated_by: string | null;
}

/** The vendor's own email domain. Accounts on it are Talogent staff. */
export const TALOGENT_DOMAIN = 'talogent.com';

export type LicenseType = 'permanent' | 'temporary';

export interface UserLicense {
  module_key: string;
  license_type: LicenseType;
  /** Five business days after the grant. Null for permanent licences. */
  expires_at: string | null;
}

export interface TemporaryLicenseRow extends UserLicense {
  user_id: number;
  full_name: string;
  email: string;
  granted_at: string;
  granted_by: string | null;
  granted_reason: string | null;
  expired: boolean;
}

/** How a module is granted when assigning it to a user. */
export interface ModuleGrant {
  type: LicenseType;
  reason?: string;
}

export interface AdminUser {
  user_id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
  is_active: number;
  is_locked: number;
  failed_login_attempts: number;
  force_password_change: number;
  last_login: string | null;
  created_at: string | null;
  has_password: boolean;
  modules: string[];
  licenses: UserLicense[];
  /**
   * The user's role WITHIN each product, keyed by module — `{ apr: 'apr_analyst' }`.
   * Separate from `role` above, which is the platform-wide one.
   */
  productRoles: Record<string, string>;
}

/** A role a product declares, offered by the user dialog once that module is ticked. */
export interface ProductRole {
  role_key: string;
  role_name: string;
  module_key: string;
  description: string | null;
}

export interface SsoConfig {
  enabled: boolean;
  protocol: string;
  issuer_url: string | null;
  client_id: string | null;
  redirect_uri: string | null;
  email_domains: string[];
  allow_local_fallback: boolean;
  auto_provision_users: boolean;
  default_role: string;
  has_client_secret: boolean;
  updated_at: string | null;
  /** Cognito-backed IdPs — see database/migrations/0045_sso_config_dual_idp.sql. */
  google_enabled: boolean;
  google_domain: string | null;
  entra_enabled: boolean;
  entra_tenant_id: string | null;
}

export interface TableUsage {
  schema_name: string;
  table_name: string;
  total_bytes: number;
  table_bytes: number;
  index_bytes: number;
  module_key: string | null;
  application: string;
}

export interface ApplicationUsage {
  module_key: string | null;
  application: string;
  schemas: string[];
  table_count: number;
  total_bytes: number;
  has_storage: boolean;
}

export interface DatabaseUsage {
  database_name: string;
  database_bytes: number;
  application_bytes: number;
  table_count: number;
  applications: ApplicationUsage[];
  tables: TableUsage[];
  captured_at: string;
}

/**
 * Administration > Open AI Integration — see apps/portal/server/src/admin/aiIntegrations.ts.
 * The screen carries the owner's name; the resource and the types keep the generic
 * AI-provider vocabulary, because OpenAI is one of the four providers it configures.
 *
 * STORAGE ONLY: nothing in the platform calls an AI provider with any of this. And
 * note what is NOT in this interface — there is no `api_key`. The server never
 * returns one, so the browser has nothing to hold.
 */
export interface AiProviderField {
  key: string;
  label: string;
  placeholder: string;
  helperText: string;
  required: boolean;
}

export interface AiProvider {
  provider: string;
  name: string;
  description: string;
  keyLabel: string;
  keyHelperText: string;
  fields: AiProviderField[];
  settings: Record<string, string>;
  /** Whether a key is stored. Never the key. */
  has_api_key: boolean;
  /** Last four characters, so an administrator can recognise which key is in place. */
  api_key_hint: string | null;
  api_key_set_at: string | null;
  api_key_set_by: string | null;
  configured: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AiModuleAssignment {
  module_key: string;
  name: string;
  /** null — and it IS stored as an absence — means "use the application default". */
  provider: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AiIntegrationConfig {
  default_provider: string | null;
  updated_at: string | null;
  updated_by: string | null;
  providers: AiProvider[];
  modules: AiModuleAssignment[];
}

export interface AuditEntry {
  id: string;
  user_id: number | null;
  /** Who performed the action. */
  actor_name: string | null;
  /** Who the action was about, when it concerns a user. */
  subject_name: string | null;
  module: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  old_values: string | null;
  new_values: string | null;
  timestamp: string;
}

// --- Application Portfolio Rationalization -----------------------------------

/** The dispositions an application can be assigned. */
export const RECOMMENDATIONS = ['Retire', 'Re-Platform', 'Re-Design', 'Retain'] as const;
export type Recommendation = (typeof RECOMMENDATIONS)[number];

export interface AprApplication {
  id: string;
  application_id_code: string;
  name: string;
  recommendation: string | null;
  description: string | null;
  technology_stack: string | null;
  user_count: number | null;
  application_owner: string | null;
  app_owner_email: string | null;
  technical_owner_custodian: string | null;
  to_email: string | null;
  /** Ownership, added 2026-08-24 (migration 0082) — see the drawer's chapters. */
  business_process_owner: string | null;
  bpo_email: string | null;
  security_owner: string | null;
  security_owner_email: string | null;
  executive_owner: string | null;
  executive_owner_email: string | null;
  application_type: string | null;
  contract_end_date: string | null;
  vendor_supplier: string | null;
  business_function_domain: string | null;
  business_criticality: string | null;
  technical_health_status: string | null;
  current_operating: string | null;
  date_of_introduction: string | null;
  overall_score: number | null;
  parent_application_id: string | null;
  /** Derived: other applications name this one as their parent. */
  is_parent: boolean;
  child_count: number;
  it_hardware_infrastructure: string | null;
  integration_dependencies: string | null;
  internal_number: string | null;
  l1_business_functional_area: string | null;
  l2_capability_function: string | null;
  license_negotiation: boolean;
  /** "Not relevant for rationalization" — see the APR Relevance block on the drawer. */
  excluded_from_apr: boolean;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  /**
   * The lifecycle the list query derives (`apr/masterData.ts` LIFECYCLE) — what the
   * Applications screen's progress strip reads. Counts arrive as strings (Postgres COUNT).
   * The last three come from Program Planning & Roadmap's tables.
   */
  qa_answered_count?: number | string | null;
  qa_total_questions?: number | string | null;
  has_initial_disposition?: boolean;
  has_ai_analysis?: boolean;
  has_final_disposition?: boolean;
  has_integration_group?: boolean;
  has_roadmap?: boolean;
  deployment_status?: string | null;
}

export interface AprRunRate {
  id: string;
  application_id: string;
  application_name: string;
  total_annual_cost: number;
  annual_software_licensing_fees: number;
  annual_third_party_vendor_support: number;
  annual_infrastructure_cost: number;
  indirect_operational_costs: number;
  cloud_saas_subscription_cost_impact: number;
  tco_status: string;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  /** From the joined application, when the reference resolves. */
  application_id_code: string | null;
}

export interface AprImportResult {
  created: number;
  updated: number;
  skipped: number;
  /** One line per rejected row, so a bad file can be fixed rather than guessed at. */
  problems: string[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
    /**
     * The response came from the proxy rather than from the API — nothing was listening.
     * Distinct from an error the API produced, which is an answer and will not change.
     */
    readonly transient = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Thin fetch wrapper.
 *
 * No retries and no fallbacks by design: if the API is unreachable or rejects a
 * request, the portal surfaces it immediately rather than masking a broken install.
 * Rejections carry the server's message so seat limits and policy violations reach
 * the user verbatim instead of becoming "something went wrong".
 */
/**
 * A failure that means NOTHING WAS LISTENING, as opposed to the API refusing the request.
 *
 * The dev proxy answers 500 with an HTML body when the API is not up; the API itself
 * answers with JSON. That difference is the only reliable signal available here, and it is
 * what `transient` on ApiError records.
 */
function isTransient(error: unknown): boolean {
  if (error instanceof ApiError) return error.transient;
  // fetch rejects outright when the connection is refused.
  return error instanceof TypeError;
}

/**
 * Retry a call that failed because nothing was listening. BOOT ONLY.
 *
 * Deliberately narrow. It does NOT retry on 4xx, and it does NOT retry an error the API
 * itself produced — those are answers, and repeating the question will not change them.
 * It covers exactly one case: the API is restarting and the portal happened to load in the
 * gap, which currently shows the user a hard error screen for something that fixes itself
 * in two seconds.
 */
async function withBootRetry<T>(call: () => Promise<T>, attempts = 3): Promise<T> {
  for (let i = 0; ; i += 1) {
    try {
      return await call();
    } catch (error) {
      if (i >= attempts - 1 || !isTransient(error)) throw error;
      await new Promise((r) => setTimeout(r, 600 * (i + 1)));
    }
  }
}

/** Fired when the server says the session is gone; the app decides where to go. */
export const SESSION_ENDED_EVENT = 'portal:session-ended';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    let details: unknown;
    // A non-JSON body on a 5xx means the response did not come from the API — it came from
    // the proxy in front of it, because the API was not there to answer.
    let fromProxy = response.status >= 500;
    try {
      const body = (await response.json()) as {
        message?: string;
        error?: string;
        details?: unknown;
      };
      message = body.message ?? body.error ?? message;
      details = body.details;
      fromProxy = false;
    } catch {
      // Non-JSON error body — keep the status line.
    }
    // The session ended (signed out in another tab, expired, or restored from the browser's cache).
    // Tell the app so it can leave, instead of leaving an old screen up with an error on it.
    if (response.status === 401 && message === 'authentication_required') {
      window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    }
    throw new ApiError(message, response.status, details, fromProxy);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

/** One band of the scoring scale, as configured in Settings → Scoring Thresholds. */
export interface AprScoringThreshold {
  label: string;
  min: number;
  max: number;
  color: string;
}

/**
 * One disposition signal. Which fields are present depends on where it came from —
 * the model and the questionnaire carry a score, the AI signal carries a rationale and
 * its drivers, a final disposition carries the source that set it.
 */
export interface AprDispositionSignal {
  disposition?: string | null;
  recommendation?: string | null;
  score?: number | null;
  status?: string | null;
  completion?: number | null;
  source?: string | null;
  color?: string;
  /** AI signal only. */
  confidence?: string | null;
  rationale?: string | null;
  drivers?: string[];
  recordedAt?: string | null;
  runId?: string | null;
}

/** The Disposition 360 payload — every signal about one application, plus the bands. */
export interface AprDispositionSummary {
  application_id: string;
  application_name: string;
  application_id_code: string | null;
  is_parent: boolean;
  thresholds: AprScoringThreshold[];
  model: AprDispositionSignal | null;
  qa: AprDispositionSignal | null;
  ai: AprDispositionSignal | null;
  manual: AprDispositionSignal | null;
  final: AprDispositionSignal | null;
  convergence: { state: 'converged' | 'divergent' | 'none'; distinct: string[]; signalCount: number };
  latestAnalysisRunId: string | null;
  latestAnalysisDisposition: string | null;
}

/** The most recent licensing analysis for one application. */
export interface AprLicensingAnalysis {
  disposition: string;
  confidence: string | null;
  risk: string | null;
  has_document: boolean;
  annual_license_cost: number | string | null;
  new_license_mid: number | string | null;
  savings_mid: number | string | null;
}

/** A parent's roll-up: its analysed children summed for the current run. */
export interface AprLicensingFamilyRollup {
  child_count: number;
  annual_license_cost: number | string | null;
  new_license_mid: number | string | null;
  savings_mid: number | string | null;
}

export interface AprLicensingForApp {
  run_id: string | null;
  analysis: AprLicensingAnalysis | null;
  /** Present only when the application has analysed children. */
  familyRollup: AprLicensingFamilyRollup | null;
}

/** One entry in a platform's capability assessment (profile.assessment). */
export interface AirPlatformAssessment {
  area?: string;
  label?: string;
  note?: string;
  rating?: number;
}

/**
 * A row of the AI platform catalogue.
 *
 * `profile` is the source's JSONB capability document and is deliberately loose: its
 * shape varies per platform and the Catalog reads only two paths out of it. Typing it
 * exactly would be inventing a contract the source does not enforce.
 */
/** One AI-platform selection criterion (Settings → AI Platform Selection Criteria). */
export interface AirPlatformCriterion {
  id: number;
  name: string;
  description: string | null;
  weight: number;
  is_selected: boolean;
  display_order: number;
}

/** A dimension's rubric — what it means and what each 1–5 level signifies. */
export interface AirDimensionRubric {
  what: string;
  levels: Record<number, string>;
}

/** The Platform Fitment Criteria payload: three tabs' worth of state under one lock. */
export interface AirFitmentCriteria {
  criteria: Array<{ id: number; dimension_code: string; name: string; priority: number }>;
  locked: boolean;
  analyzedCount: number;
  rubric: Record<string, AirDimensionRubric>;
  rubricFallback: AirDimensionRubric;
  factors: Array<{ code: string; name: string; what: string }>;
  clientProfile: string;
  weightCapability: number;
}

/**
 * The company a discovery runs against — read from Administration → Company Profile.
 *
 * NOT a choice on the screen. This install serves one client, and a discovery aimed at
 * anyone else would be reporting on the wrong business (owner's ruling, 2026-08-04).
 */
export interface SpnDiscoveryCompany {
  companyName: string;
  /** Most significant first, as Company Profile stores them. */
  sicCodes: string[];
}

/**
 * One knob on an instruction module.
 *
 * `directives` is the point of it: every value carries the sentence injected into the
 * compiled prompt when it is chosen. A knob whose values did not change the text sent
 * would be a control that decorates a form.
 */
export interface SpnInstructionKnob {
  key: string;
  label: string;
  hint?: string;
  values: string[];
  /** What to show for each value — "deep — up to 20 per area". Falls back to the value. */
  labels?: Record<string, string>;
  default?: string;
  directives?: Record<string, string>;
}

/** An editable analysis module with a revertable shipped default. */
export interface SpnInstructionModule {
  id: string;
  prompt_key: string | null;
  seq: number;
  name: string;
  description: string | null;
  /** The business area it belongs to, or null for the base module, which applies to all. */
  business_area: string | null;
  body: string;
  /** What shipped. `null` for a module the client created. */
  default_body: string | null;
  config_schema: SpnInstructionKnob[] | null;
  config: Record<string, string> | null;
  is_system: boolean;
  updated_at: string | null;
}

/**
 * One row of Discovery History.
 *
 * `opp_count` is every opportunity the session produced; `created_count` only those
 * already committed to the real opportunities table — the "Opportunities" and "Saved"
 * columns, which look alike and are not.
 */
export interface SpnDiscoverySession {
  id: number;
  session_code: string;
  resolved_company_name: string;
  company_code: string | null;
  round_number: number | null;
  status: string;
  opp_count: number | string;
  created_count: number | string;
  creator_name: string | null;
  created_at: string;
}

/**
 * One discovery session as the results screen sees it.
 *
 * `status` is the run's own state: `running` while Claude is generating, `active` once a
 * round has returned, `failed` with `run_error` set when it did not.
 */
export interface SpnDiscoverySessionDetail {
  id: number;
  session_code: string;
  company_name: string;
  company_sic_codes: string[] | null;
  business_areas: string[] | null;
  custom_areas: string[] | null;
  business_units: string[] | null;
  instructions_used: string | null;
  status: string;
  run_error: string | null;
  run_started: string | null;
  round_number: number;
  creator_name: string | null;
  created_at: string;
}

/** One low / medium / high case: what it is worth, and what that scope actually is. */
export interface SpnCase<V = string> {
  value: V | null;
  justification: string;
}

/**
 * One dimension of an opportunity, as three scopes.
 *
 * `tone` rates how FAVOURABLE the medium case is, never how large — a low cost is good,
 * a low benefit is not. It drives the tint and nothing else.
 */
export interface SpnDimension<V = string> {
  tone: 'good' | 'moderate' | 'poor';
  /**
   * Prose about THIS dimension — what drives the timeline, what the benefit rests on.
   * Distinct from the opportunity's `additional_comments`, which describes the finding.
   */
  narrative?: string;
  low: SpnCase<V>;
  medium: SpnCase<V>;
  high: SpnCase<V>;
}

/** The five dimensions. Absent on opportunities produced before this shape existed. */
export interface SpnCases {
  benefit: SpnDimension | null;
  roi: SpnDimension | null;
  risk: SpnDimension | null;
  cost: SpnDimension | null;
  /** Implementation effort in whole weeks — NOT the regulatory wait, which is `gating`. */
  timeline: SpnDimension<number> | null;
}

export type SpnDimensionKey = keyof SpnCases;
export type SpnCaseLevel = 'low' | 'medium' | 'high';

/**
 * One candidate opportunity.
 *
 * `ebitda_impact` is null when the model could not size it — which is not the same as
 * zero, and must not be rendered as zero.
 */
export interface SpnDiscoveryOpportunity {
  id: number;
  business_area: string;
  /** One of the client's declared units, or null when they declared none. */
  business_unit: string | null;
  description: string;
  /** The long-form detail behind the one-line description. */
  additional_comments: string | null;
  /** What is leaking, and the mechanism by which it leaks. */
  rationale: string | null;
  /** The arithmetic behind the figure — checkable by someone who knows the business. */
  basis: string | null;
  /** What had to be assumed, named so it can be argued with directly. */
  assumptions: string | null;
  /** The mid case. `ebitda_low`/`ebitda_high` bound it; all three null = could not size. */
  ebitda_impact: string | number | null;
  ebitda_low: string | number | null;
  ebitda_high: string | number | null;
  confidence: string | null;
  /** Why that confidence. A bare high/medium/low cannot be acted on. */
  confidence_reason: string | null;
  time_to_impact: string | null;
  gating: string | null;
  first_step: string | null;
  score_rationale: string | null;
  cases: SpnCases | null;
  /** Which case the client is carrying per dimension. Medium unless changed. */
  chosen: Record<SpnDimensionKey, SpnCaseLevel>;
  /** Ticked for inclusion in the Word / PowerPoint output. */
  selected: boolean;
  /** 1 as generated; incremented by each accepted re-run. */
  version: number;
  /** What the client asked to be tested on the most recent re-run. */
  client_challenge: string | null;
  /** The model's written answer to that challenge, including where it declined to move. */
  revision_verdict: string | null;
  /** `idle` | `running` | `failed` — a re-run is polled, not held open. */
  revision_status: string;
  revision_error: string | null;
  revised_at: string | null;
  /** The opportunity this is a variant of, or null when it is the original. */
  variant_of_id: number | null;
  variant_label: string | null;
  /** Non-null means LOCKED — it lives in another product and cannot be edited here. */
  transferred_at: string | null;
  transferred_to: string | null;
  /** The receiving product's own id for its copy. A string, never a foreign key. */
  transfer_ref: string | null;
  /** Set when a transfer was undone. Survives the release, so the history is not erased. */
  released_at: string | null;
  round_number: number;
  is_unexpected: boolean;
  ai_alignment_score: number | null;
  benefit_cost_effectiveness_score: number | null;
  delivery_risk_score: number | null;
  created_opportunity_id: string | null;
}

/** A reusable challenge — the argument you make of a finding, saved so it stays consistent. */
export interface SpnChallenge {
  id: string;
  seq: number;
  name: string;
  body: string;
  /** What shipped. Null for one the client wrote. */
  default_body: string | null;
  is_system: boolean;
  /** Drives the library's order — what actually gets used floats up. */
  use_count: number;
}

/** A superseded state of an opportunity, whole. */
export interface SpnOpportunityVersion {
  version: number;
  snapshot: Record<string, unknown>;
  client_challenge: string | null;
  revision_verdict: string | null;
  created_at: string;
}

/** One steer of a session: what was asked, and what came back. */
export interface SpnDiscoveryRound {
  round_number: number;
  instruction: string | null;
  areas_added: string[] | null;
  opportunities_returned: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string;
}

export interface AirFitOpportunity {
  opportunity_id: string;
  description: string;
  analyzed: boolean;
  bestPlatform: string | null;
  execSummary: string | null;
  fitScore: number | null;
  readinessScore: number | null;
  overallScore: number | null;
}

export interface AirFitAnalysis {
  executive_summary?: string;
  business_drivers?: { requirement: string; importance: string; assessment: string }[];
  measurable?: {
    rows?: { area: string; importance: string; score: string; justification: string }[];
    narrative?: string;
  };
  strengths?: { category: string; points: string[] }[];
  limitations?: { category: string; points: string[] }[];
  risks?: { risk: string; impact: string; mitigation: string }[];
  adoption_considerations?: string;
}

export interface AirFitSummaryOpportunity {
  opportunity_id: string;
  description: string;
  bestFit: string | null;
  assigned: boolean;
  lockedPlatformId: number | null;
  /** The platform behind `bestFit` (assigned, else justified, else top-ranked): what Assign sends. */
  bestFitPlatformId: number | null;
  explanation: string | null;
  analysis: AirFitAnalysis | null;
  fitScore: number | null;
  readinessScore: number | null;
  overallScore: number | null;
}

export interface AirFitSummaryGroup {
  project_id: string;
  description: string;
  opps: AirFitSummaryOpportunity[];
}

/**
 * What an AI Platform Fitness Analysis is measured against — the three tabs of Platform
 * Fitment Criteria, in one object so the screen can show its own basis rather than
 * presenting scores with no provenance.
 */
export interface AirFitmentStandard {
  /** Platform Capabilities tab: the selected criteria and their weights. */
  criteria: { id: number; name: string; description: string | null; weight: number }[];
  /** Organizational Readiness tab: the free-text client environment profile. */
  clientProfile: string;
  /** Weighting tab, as a split that always sums to 100. */
  weightCapability: number;
  weightReadiness: number;
  /** The three fixed readiness axes the profile is read against. */
  readinessFactors: { name: string; what: string }[];
}

export interface AirFitProjectGroup {
  project_id: string;
  description: string;
  opps: AirFitOpportunity[];
}

export interface AirPlatform {
  id: number;
  platform_code: string;
  name: string;
  vendor: string | null;
  category: string | null;
  positioning: string | null;
  source: string;
  profile: {
    positioning?: string;
    assessment?: AirPlatformAssessment[];
    [key: string]: unknown;
  } | null;
}

export const api = {
  // Retried at boot: the portal loading in the two seconds an API restart takes should not
  // present the user with a hard error for something that fixes itself.
  session: () => withBootRetry(() => request<SessionResponse>('/api/session')),
  modules: () => withBootRetry(() => request<ModulesResponse>('/api/modules')),
  login: (identifier: string, password: string) =>
    request<{ user: SessionUser }>('/api/auth/login', json('POST', { identifier, password })),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/auth/change-password', json('POST', { currentPassword, newPassword })),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  admin: {
    getCompany: () => request<CompanyProfile>('/api/admin/company'),
    updateCompany: (update: Partial<Record<string, unknown>>) =>
      request<CompanyProfile>('/api/admin/company', json('PUT', update)),

    getEntitlements: () => request<{ entitlements: EntitlementRow[] }>('/api/admin/entitlements'),
    updateEntitlement: (
      moduleKey: string,
      update: {
        enabled?: boolean;
        seatLimit?: number;
        temporarySeatLimit?: number;
        termStart?: string | null;
        termEnd?: string | null;
      },
    ) => request<EntitlementRow>(`/api/admin/entitlements/${moduleKey}`, json('PUT', update)),

    getTemporaryLicenses: () =>
      request<{ licenses: TemporaryLicenseRow[] }>('/api/admin/licenses/temporary'),

    /** `talogent` is honoured only for vendor staff; anyone else gets client users. */
    getUsers: (scope: 'client' | 'talogent' = 'client') =>
      request<{ users: AdminUser[] }>(`/api/admin/users?scope=${scope}`),
    getTalogentUsers: () => request<{ users: AdminUser[] }>('/api/admin/talogent-users'),
    createUser: (input: Record<string, unknown>) =>
      request<AdminUser>('/api/admin/users', json('POST', input)),
    updateUser: (userId: number, update: Record<string, unknown>) =>
      request<AdminUser>(`/api/admin/users/${userId}`, json('PUT', update)),
    deleteUser: (userId: number) =>
      request<void>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
    getProductRoles: () => request<{ roles: ProductRole[] }>('/api/admin/product-roles'),
    setUserModules: (
      userId: number,
      modules: string[],
      grants: Record<string, ModuleGrant> = {},
      productRoles: Record<string, string> = {},
    ) =>
      request<AdminUser>(
        `/api/admin/users/${userId}/modules`,
        json('PUT', { modules, grants, productRoles }),
      ),
    lockUser: (userId: number) =>
      request<AdminUser>(`/api/admin/users/${userId}/lock`, { method: 'POST' }),
    unlockUser: (userId: number) =>
      request<AdminUser>(`/api/admin/users/${userId}/unlock`, { method: 'POST' }),
    resetPassword: (userId: number, password: string) =>
      request<void>(`/api/admin/users/${userId}/reset-password`, json('POST', { password })),

    getSso: () => request<SsoConfig>('/api/admin/sso'),
    updateSso: (update: Record<string, unknown>) =>
      request<SsoConfig>('/api/admin/sso', json('PUT', update)),

    /*
      Open AI Integration. Every write returns the whole configuration, because a
      provider's `configured` flag and a module's effective provider both depend on
      values outside the row being written.
    */
    getAiIntegrations: () => request<AiIntegrationConfig>('/api/admin/ai-integrations'),
    /** `apiKey` is write-only — send a new one, or omit it to leave the stored one. */
    updateAiProvider: (
      provider: string,
      update: { settings?: Record<string, string>; apiKey?: string; clearApiKey?: boolean },
    ) =>
      request<AiIntegrationConfig>(
        `/api/admin/ai-integrations/providers/${provider}`,
        json('PUT', update),
      ),
    setAiDefaultProvider: (provider: string | null) =>
      request<AiIntegrationConfig>('/api/admin/ai-integrations/default', json('PUT', { provider })),
    /** `null` clears the override, and the module inherits the application default. */
    setAiModuleProvider: (moduleKey: string, provider: string | null) =>
      request<AiIntegrationConfig>(
        `/api/admin/ai-integrations/modules/${moduleKey}`,
        json('PUT', { provider }),
      ),

    getDatabaseUsage: () => request<DatabaseUsage>('/api/admin/database-usage'),

    getAudit: (options: { limit?: number; from?: string; to?: string } = {}) => {
      const params = new URLSearchParams({ limit: String(options.limit ?? 200) });
      if (options.from) params.set('from', options.from);
      if (options.to) params.set('to', options.to);
      return request<{ entries: AuditEntry[] }>(`/api/admin/audit?${params.toString()}`);
    },
  },

  aiReadiness: {
    /** Strategic Intelligence → AI Platforms Catalog. */
    getPlatforms: () => request<{ platforms: AirPlatform[] }>('/api/ai_readiness/platforms'),
    getPlatform: (id: number | string) =>
      request<{ platform: AirPlatform; profile: Record<string, unknown> }>(
        `/api/ai_readiness/platforms/${id}`,
      ),


    /** Strategic Intelligence → AI Platform detail: ask a question, or pull fresh data. */
    askPlatform: (id: number | string, question: string) =>
      request<{ answer: string }>(
        `/api/ai_readiness/platforms/${id}/ask`,
        json('POST', { question }),
      ),
    refreshPlatform: (id: number | string) =>
      request<{ ok: boolean }>(`/api/ai_readiness/platforms/${id}/refresh`, json('POST', {})),

    /** Settings → AI Platform Selection → Platform Fitment Criteria (Platform Capabilities tab). */
    getPlatformCriteria: () =>
      request<{ criteria: AirPlatformCriterion[] }>('/api/ai_readiness/platform-criteria'),
    savePlatformCriteria: (
      items: ReadonlyArray<{ id: number; selected: boolean; weight: number }>,
    ) =>
      request<{ saved: number }>('/api/ai_readiness/platform-criteria', json('PUT', { items })),
    addPlatformCriterion: (name: string, description: string) =>
      request<{ created: boolean }>(
        '/api/ai_readiness/platform-criteria',
        json('POST', { name, description }),
      ),
    deletePlatformCriterion: (id: number) =>
      request<{ deleted: boolean }>(`/api/ai_readiness/platform-criteria/${id}`, {
        method: 'DELETE',
      }),

    /**
     * Strategic Intelligence → Platform Analysis.
     *
     * The run is NOT awaited: `startPlatformAnalysis` returns a job id within
     * milliseconds and the screen polls `getPlatformAnalysisStatus`, because the three
     * model calls behind it take up to a minute.
     */
    getPlatformAnalysisCriteria: () =>
      request<{
        criteriaCount: number;
        criteria: Array<{ id: number; name: string; description: string | null; weight: number }>;
      }>('/api/ai_readiness/platform-analysis/criteria'),
    startPlatformAnalysis: (name: string, context: string) =>
      request<{ jobId: string; name: string }>(
        '/api/ai_readiness/platform-analysis',
        json('POST', { name, context }),
      ),
    getPlatformAnalysisStatus: (jobId: string) =>
      request<{ status: string; name?: string; platformId?: number; error?: string }>(
        `/api/ai_readiness/platform-analysis/status/${jobId}`,
      ),

    /**
     * Strategic Intelligence → AI Platform Fit.
     *
     * The batch is sequential and backgrounded: `runFit` returns a job id, the screen polls
     * `getFitStatus`, and `stopFit` aborts the opportunity in flight WITHOUT saving it —
     * everything finished before the stop is already committed and kept.
     */
    getPlatformFit: () =>
      request<{
        projectGroups: AirFitProjectGroup[];
        criteriaLocked: boolean;
        weightCapability: number;
        /** The three tabs of Platform Fitment Criteria, as the run will use them. */
        standard: AirFitmentStandard;
      }>('/api/ai_readiness/platform-fit'),
    runFit: (opportunityIds: readonly string[]) =>
      request<{ ok: boolean; jobId: string; total: number }>(
        '/api/ai_readiness/platform-fit/analyze',
        json('POST', { opportunity_ids: opportunityIds }),
      ),
    getFitStatus: (jobId: string) =>
      request<{
        status: string; total: number; done: number; current: string | null;
        error: string | null; failedOn: string | null;
      }>(`/api/ai_readiness/platform-fit/analyze-status/${jobId}`),
    stopFit: (jobId: string) =>
      request<{ ok: boolean; status: string; done: number }>(
        `/api/ai_readiness/platform-fit/analyze-stop/${jobId}`,
        json('POST', {}),
      ),

    /** Strategic Intelligence → Platform Fit Summary. */
    getPlatformFitSummary: () =>
      request<{ projectGroups: AirFitSummaryGroup[]; weightCapability: number }>(
        '/api/ai_readiness/platform-fit/summary',
      ),
    getFitExportStatus: (opportunityIds: readonly string[]) =>
      request<{
        ok: boolean; analyzed: number; covered: number;
        word: { filename: string; size: number } | null;
        ppt: { filename: string; size: number } | null;
      }>(
        '/api/ai_readiness/platform-fit/summary/export-status',
        json('POST', { opportunity_ids: opportunityIds }),
      ),
    /**
     * Generate (or re-download) the Word report / PowerPoint deck. Returns a Blob, because
     * the response is the document itself — the server streams bytes, not JSON.
     */
    exportFitSummary: async (opportunityIds: readonly string[], format: 'word' | 'ppt') => {
      const response = await fetch('/api/ai_readiness/platform-fit/summary/export', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunity_ids: opportunityIds, format }),
      });
      if (!response.ok) {
        let message = `${response.status} ${response.statusText}`;
        try {
          const body = (await response.json()) as { error?: string };
          message = body.error ?? message;
        } catch {
          /* a non-JSON body means the proxy answered, not the API */
        }
        throw new Error(message);
      }
      const name =
        /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
        `platform-fit.${format === 'ppt' ? 'pptx' : 'docx'}`;
      return { blob: await response.blob(), filename: name };
    },

    assignPlatform: (body: {
      opportunity_id: string;
      platform_id: number | null;
      justification?: string;
    }) =>
      request<{ ok: boolean; assigned?: boolean; unchanged?: boolean }>(
        '/api/ai_readiness/platform-fit/lock',
        json('POST', body),
      ),

    /** Strategic Intelligence → Platform Fitment Criteria. */
    getFitmentCriteria: () =>
      request<AirFitmentCriteria>('/api/ai_readiness/fitment-criteria'),
    saveFitmentCriteria: (body: { clientProfile: string; weightCapability: number }) => request<{ saved: boolean }>('/api/ai_readiness/fitment-criteria', json('PUT', body)),
    toggleFitmentLock: () =>
      request<{ locked: boolean }>('/api/ai_readiness/fitment-criteria/lock', json('POST', {})),
  },

  spn: {
    /** Strategic Portfolio Navigator → Discoveries. */
    getDiscoverySessions: () =>
      request<{ sessions: SpnDiscoverySession[] }>('/api/spn/discovery/sessions'),
    /** Everything the New Discovery screen needs, in one round trip. */
    getNewDiscovery: () =>
      request<{
        company: SpnDiscoveryCompany;
        businessAreas: string[];
        modules: SpnInstructionModule[];
        tokenBudget: number;
      }>('/api/spn/discovery/new'),

    /** Starts a run and returns immediately — poll `getDiscoverySession` for the result. */
    startDiscovery: (input: {
      moduleIds: string[];
      areas: string[];
      customAreas: string[];
      businessUnits: string[];
      overrides?: Record<string, Record<string, string>>;
    }) => request<{ sessionCode: string }>('/api/spn/discovery/sessions', json('POST', input)),

    getDiscoverySession: (code: string) =>
      request<{
        session: SpnDiscoverySessionDetail;
        opportunities: SpnDiscoveryOpportunity[];
        rounds: SpnDiscoveryRound[];
      }>(`/api/spn/discovery/sessions/${encodeURIComponent(code)}`),

    /** Tick or untick one opportunity for the output. */
    selectOpportunity: (id: number, selected: boolean) =>
      request<{ selected: boolean }>(
        `/api/spn/discovery/opportunities/${id}/select`,
        json('POST', { selected }),
      ),

    /** Carry a different case on one dimension. */
    chooseCase: (id: number, dimension: SpnDimensionKey, level: SpnCaseLevel) =>
      request<{ dimension: string; level: string }>(
        `/api/spn/discovery/opportunities/${id}/case`,
        json('POST', { dimension, level }),
      ),

    /** Tick or untick everything, or everything in one business area. */
    selectAll: (code: string, input: { selected: boolean; businessArea?: string }) =>
      request<{ selected: boolean; changed: number }>(
        `/api/spn/discovery/sessions/${encodeURIComponent(code)}/select-all`,
        json('POST', input),
      ),

    /** Save the client's edits. Every field is optional; absent fields are left alone. */
    updateOpportunity: (
      id: number,
      patch: Partial<{
        description: string;
        additional_comments: string;
        rationale: string;
        basis: string;
        assumptions: string;
        confidence: string;
        confidence_reason: string;
        gating: string;
        first_step: string;
        cases: unknown;
        ebitda_low: number | null;
        ebitda_mid: number | null;
        ebitda_high: number | null;
      }>,
    ) =>
      request<{ opportunity: SpnDiscoveryOpportunity }>(
        `/api/spn/discovery/opportunities/${id}`,
        json('PATCH', patch),
      ),

    /** Send this one opportunity back through the same modules. Returns immediately. */
    rerunOpportunity: (
      id: number,
      input: { challenge: string; challengeId?: string; label?: string },
    ) =>
      request<{ started: boolean }>(
        `/api/spn/discovery/opportunities/${id}/rerun`,
        json('POST', input),
      ),

    getOpportunityVersions: (id: number) =>
      request<{ versions: SpnOpportunityVersion[] }>(
        `/api/spn/discovery/opportunities/${id}/versions`,
      ),


    deleteOpportunity: (id: number) =>
      request<{ deleted: boolean }>(`/api/spn/discovery/opportunities/${id}`, {
        method: 'DELETE',
      }),


    /** Everything the Transfer screen shows, in the two sets it shows them in. */
    getTransferBoard: () =>
      request<{
        ready: SpnDiscoveryOpportunity[];
        transferred: SpnDiscoveryOpportunity[];
      }>('/api/spn/discovery/transfer'),

    /** Send opportunities and lock them here. Locks only what actually went. */
    transferOpportunities: (ids: number[], target = 'portfolio_definition') =>
      request<{
        transferred: Array<{ id: number; ref: string }>;
        refused: Array<{ id: number; reason: string }>;
      }>('/api/spn/discovery/transfer', json('POST', { ids, target })),

    /** The release connector — the receiving product deleted its copy. */
    releaseTransfer: (ref: string, note?: string) =>
      request<{ released: boolean; id: number }>(
        '/api/spn/discovery/transfer/release',
        json('POST', { ref, ...(note ? { note } : {}) }),
      ),

    /**
     * Download the Word or PowerPoint deliverable for what is ticked.
     *
     * A BLOB and a synthetic anchor, not `window.open`: the request carries the session
     * cookie, and the filename comes from the server's Content-Disposition rather than
     * being guessed from the URL.
     */
    downloadExport: async (format: 'docx' | 'pptx', sessionCode?: string) => {
      const qs = sessionCode ? `?session=${encodeURIComponent(sessionCode)}` : '';
      const response = await fetch(`/api/spn/discovery/export.${format}${qs}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        // The server answers a refusal in JSON; anything else is a genuine failure.
        let message = `${response.status} ${response.statusText}`;
        try {
          const body = (await response.json()) as { error?: string };
          message = body.error ?? message;
        } catch {
          /* keep the status line */
        }
        throw new ApiError(message, response.status);
      }

      const disposition = response.headers.get('content-disposition') ?? '';
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match?.[1] ?? `strategic-discoveries.${format}`;

      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      // Deferred: revoking synchronously races the download in some browsers.
      window.setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
    },

    /** The challenge library. */
    getChallenges: () => request<{ challenges: SpnChallenge[] }>('/api/spn/discovery/challenges'),
    createChallenge: (input: { name: string; body: string }) =>
      request<{ challenge: SpnChallenge }>('/api/spn/discovery/challenges', json('POST', input)),
    updateChallenge: (id: string, input: { name?: string; body?: string }) =>
      request<{ challenge: SpnChallenge }>(
        `/api/spn/discovery/challenges/${id}`,
        json('PATCH', input),
      ),
    deleteChallenge: (id: string) =>
      request<{ deleted?: boolean; hidden?: boolean }>(`/api/spn/discovery/challenges/${id}`, {
        method: 'DELETE',
      }),

    /** Rule on every pending opportunity at once, optionally narrowed to one business area. */
    decideAll: (
      code: string,
      input: {
        decision: 'kept' | 'discarded' | 'pending';
        businessArea?: string;
        /** Default true — leaves decisions already made alone. */
        onlyPending?: boolean;
      },
    ) =>
      request<{ decision: string; changed: number }>(
        `/api/spn/discovery/sessions/${encodeURIComponent(code)}/decide-all`,
        json('POST', input),
      ),

    /** Deletes the session and everything under it. */
    deleteDiscovery: (code: string) =>
      request<{ deleted: boolean }>(`/api/spn/discovery/sessions/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      }),

    steerDiscovery: (code: string, input: { instruction: string; areasAdded: string[] }) =>
      request<{ round: number }>(
        `/api/spn/discovery/sessions/${encodeURIComponent(code)}/steer`,
        json('POST', input),
      ),

    /** The instruction module library. */
    getInstructions: () =>
      request<{ modules: SpnInstructionModule[] }>('/api/spn/discovery/instructions'),
    createInstruction: (input: {
      name: string;
      description?: string;
      /** Files the module into a group; omit to leave it ungrouped. */
      business_area?: string | null;
      /** Sequences the new module immediately after this one, so a copy sits by its source. */
      after_seq?: number;
      body?: string;
      config_schema?: SpnInstructionKnob[];
      config?: Record<string, string>;
    }) =>
      request<{ module: SpnInstructionModule }>(
        '/api/spn/discovery/instructions',
        json('POST', input),
      ),
    updateInstruction: (
      id: string,
      update: { name?: string; description?: string; body: string; config?: Record<string, string> },
    ) =>
      request<{ module: SpnInstructionModule }>(
        `/api/spn/discovery/instructions/${id}`,
        json('PATCH', update),
      ),
    resetInstruction: (id: string) =>
      request<{ module: SpnInstructionModule }>(
        `/api/spn/discovery/instructions/${id}/reset`,
        json('POST', {}),
      ),
    /**
     * The prompt as it would be sent — compiled on the server so the text shown is the
     * text used. The company is not sent: it comes from Company Profile at both ends.
     */
    compileDiscoveryPrompt: (input: {
      moduleIds: string[];
      areas: string[];
      customAreas: string[];
      overrides?: Record<string, Record<string, string>>;
    }) =>
      request<{ prompt: string; moduleCount: number }>(
        '/api/spn/discovery/compile',
        json('POST', input),
      ),

    deleteInstruction: (id: string) =>
      request<{ deleted?: boolean; hidden?: boolean }>(
        `/api/spn/discovery/instructions/${id}`,
        { method: 'DELETE' },
      ),
  },

  apr: {
    // Paths mirror the server exactly: /api/apr/applications and
    // /api/apr/current-run-rate, with `all` and the bulk routes ahead of `:id`.
    getApplications: () => request<{ applications: AprApplication[] }>('/api/apr/applications'),
    createApplication: (input: Record<string, unknown>) =>
      request<AprApplication>('/api/apr/applications', json('POST', input)),
    updateApplication: (id: string, update: Record<string, unknown>) =>
      request<AprApplication>(`/api/apr/applications/${id}`, json('PUT', update)),
    deleteApplication: (id: string) =>
      request<void>(`/api/apr/applications/${id}`, { method: 'DELETE' }),
    /**
     * Every disposition signal for one application — model, Q&A, AI and manual — plus
     * the scoring thresholds that colour and band them. Feeds the Disposition 360 card.
     */
    getDispositionSummary: (id: string) =>
      request<AprDispositionSummary>(`/api/apr/applications/${id}/disposition-360`),
    /** The most recent licensing analysis for one application, with its levers and history. */
    getLicensingForApp: (id: string) =>
      request<AprLicensingForApp>(`/api/apr/licensing/app/${id}`),
    /** Create from file. Existing Application IDs are skipped, not overwritten. */
    bulkImportApplications: (applications: Record<string, unknown>[]) =>
      request<AprImportResult>('/api/apr/applications/bulk-import', json('POST', { applications })),
    /** Update from file, matched on Application ID. */
    bulkUpdateApplications: (updates: Record<string, unknown>[]) =>
      request<AprImportResult>('/api/apr/applications/bulk-update', json('POST', { updates })),
    /** Administrator only. Soft — the rows stay in the audit trail. */
    deleteAllApplications: () =>
      request<{ deletedCount: number }>('/api/apr/applications/all', { method: 'DELETE' }),

    getRunRate: () => request<{ costs: AprRunRate[] }>('/api/apr/current-run-rate'),
    /** Applications with no cost record yet — the Add Cost dropdown. */
    getAvailableApplications: () =>
      request<{
        applications: { id: string; application_id_code: string; application_name: string }[];
      }>('/api/apr/current-run-rate/available-applications'),
    createRunRate: (input: Record<string, unknown>) =>
      request<AprRunRate>('/api/apr/current-run-rate', json('POST', input)),
    updateRunRate: (id: string, update: Record<string, unknown>) =>
      request<AprRunRate>(`/api/apr/current-run-rate/${id}`, json('PUT', update)),
    deleteRunRate: (id: string) =>
      request<void>(`/api/apr/current-run-rate/${id}`, { method: 'DELETE' }),
    bulkImportRunRate: (currentRunRateData: Record<string, unknown>[]) =>
      request<AprImportResult>(
        '/api/apr/current-run-rate/bulk-import',
        json('POST', { currentRunRateData }),
      ),
    bulkUpdateRunRate: (currentRunRateData: Record<string, unknown>[]) =>
      request<AprImportResult>(
        '/api/apr/current-run-rate/bulk-update',
        json('PUT', { currentRunRateData }),
      ),
    deleteAllRunRate: () =>
      request<{ deletedCount: number }>('/api/apr/current-run-rate/all', { method: 'DELETE' }),
  },
};
